/**
 * GraphQL-backed Tasks Report macro renderer.
 *
 * Drop-in replacement for TasksReportMacroRenderer that fetches task data via
 * the `inlineTasks` AGG GraphQL query instead of the legacy REST endpoint.
 *
 * Usage: wire this component into the extension handler instead of
 * TasksReportMacroRenderer once you're ready to flip the feature gate.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import Spinner from '@atlaskit/spinner/spinner';
import { token } from '@atlaskit/tokens';

import type { ExtensionHandlerProps } from '@confluence/fabric-extension-lib/entry-points/commonExtensionTypes.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { GenericErrorBoundary } from '@confluence/error-boundary/entry-points/GenericErrorBoundary.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import {
	getExperienceName,
	getMacroAttributesFromADFNode,
} from '@confluence/macro-tracker/entry-points/MacroExperienceTracker.ts';
import { MacroExperienceFailure } from '@confluence/macro-tracker/entry-points/MacroExperienceFailure.ts';
import { MacroExperienceSuccess } from '@confluence/macro-tracker/entry-points/MacroExperienceSuccess.ts';

import { parseMacroParams } from './hooks/useParsedMacroParams.ts';
import type { CompleteTaskTrigger } from './hooks/useCompleteTask.ts';
import { useTasksReportData } from './hooks/useTasksReportData.ts';
import { TasksReportTable } from './components/TasksReportTable.tsx';
import { EmptyState } from './components/EmptyState.tsx';
import type { RawMacroParams } from './hooks/useParsedMacroParams.ts';
import { i18n } from './tasksReportMacroI18n.ts';

export type TasksReportMacroRendererProps = {
	macroDefaultProps: ExtensionHandlerProps;
	macroParams?: RawMacroParams;
};

type SortState = {
	sortBy: string | undefined;
	reverseSort: boolean;
};

const styles = cssMap({
	container: {
		display: 'block',
		width: '100%',
	},
	loading: {
		display: 'flex',
		justifyContent: 'center',
		paddingTop: token('space.300'),
		paddingBottom: token('space.300'),
	},
});

const TasksReportMacroRendererComponent = ({
	macroDefaultProps,
	macroParams,
}: TasksReportMacroRendererProps) => {
	const { formatMessage } = useIntl();
	const params = useMemo(() => parseMacroParams(macroParams), [macroParams]);
	const { cloudId } = useSessionData();

	const { contentId, extensionKey, mode, node } = macroDefaultProps;
	const attributes = macroDefaultProps.attributes ?? getMacroAttributesFromADFNode(node);
	const experienceName = getExperienceName(mode, node);

	// Track sort state locally so the user can click column headers without
	// changing the macro definition. Reset to macro defaults when params change
	// (e.g. macro is re-configured). Stored as a single object to avoid chained
	// state updates (no-chain-state-updates lint rule).
	const [sortState, setSortState] = useState<SortState>({
		sortBy: params.sortBy,
		reverseSort: params.reverseSort,
	});

	useEffect(() => {
		setSortState({ sortBy: params.sortBy, reverseSort: params.reverseSort });
	}, [params.sortBy, params.reverseSort]);

	const effectiveParams = useMemo(
		() => ({ ...params, sortBy: sortState.sortBy, reverseSort: sortState.reverseSort }),
		[params, sortState],
	);

	const taskUpdateTrigger = useMemo<CompleteTaskTrigger>(() => {
		switch (macroDefaultProps.mode) {
			case 'editor':
				return 'EDIT_PAGE';
			case 'renderer':
				return 'VIEW_PAGE';
			default:
				return 'UNKNOWN';
		}
	}, [macroDefaultProps.mode]);

	const { tasks, loading, resolving, error, hasAnyScopeFilter, hasNextPage, loadMore, refetch } =
		useTasksReportData({
			params: effectiveParams,
			cloudId,
		});

	// Optimistically hide tasks that the user has just completed so they
	// disappear immediately while the refetch is in flight.
	const [optimisticallyHiddenTaskIds, setOptimisticallyHiddenTaskIds] = useState<Set<string>>(
		() => new Set(),
	);

	const visibleTasks = useMemo(() => {
		if (optimisticallyHiddenTaskIds.size === 0) {
			return tasks;
		}

		return tasks.filter((task) => !optimisticallyHiddenTaskIds.has(task.id));
	}, [optimisticallyHiddenTaskIds, tasks]);

	const handleTaskComplete = useCallback(
		async (taskId: string) => {
			setOptimisticallyHiddenTaskIds((previousHiddenIds) => {
				if (previousHiddenIds.has(taskId)) {
					return previousHiddenIds;
				}

				const nextHiddenIds = new Set(previousHiddenIds);
				nextHiddenIds.add(taskId);
				return nextHiddenIds;
			});

			try {
				await refetch();
			} finally {
				setOptimisticallyHiddenTaskIds((previousHiddenIds) => {
					if (!previousHiddenIds.has(taskId)) {
						return previousHiddenIds;
					}

					const nextHiddenIds = new Set(previousHiddenIds);
					nextHiddenIds.delete(taskId);
					return nextHiddenIds;
				});
			}
		},
		[refetch],
	);

	// sortKey is a table column key (e.g. 'duedate'), not an AGG enum or the saved macro
	// value ("due date"). The hook maps it to the correct GraphQL enum value internally.
	// Both sortBy and reverseSort are updated together in a single setState call
	// to satisfy the no-chain-state-updates lint rule.
	const handleSort = useCallback((sortKey: string) => {
		setSortState((prev) => ({
			sortBy: sortKey,
			reverseSort: prev.sortBy === sortKey ? !prev.reverseSort : false,
		}));
	}, []);

	if ((loading || resolving) && visibleTasks.length === 0) {
		return (
			<Box xcss={styles.loading} testId="tasks-report-loading" data-render-path="rewrite-graphql">
				<Spinner size="medium" />
			</Box>
		);
	}

	if (error) {
		return (
			<>
				<MacroExperienceFailure
					name={experienceName}
					contentId={contentId}
					extensionKey={extensionKey}
					mode={mode}
					attributes={attributes}
					error={error}
				/>
				<ErrorDisplay error={error}>{formatMessage(i18n.errorLoading)}</ErrorDisplay>
			</>
		);
	}

	if (!loading && !resolving && visibleTasks.length === 0) {
		return (
			<>
				<MacroExperienceSuccess
					name={experienceName}
					contentId={contentId}
					extensionKey={extensionKey}
					mode={mode}
					attributes={attributes}
				/>
				<EmptyState status={params.status} noSpacesSelected={!hasAnyScopeFilter} />
			</>
		);
	}

	return (
		<Box
			xcss={styles.container}
			testId="tasks-report-macro-container"
			data-render-path="rewrite-graphql"
			data-fabric-macro={macroDefaultProps?.attributes?.macroId}
			data-macro-body=""
		>
			<MacroExperienceSuccess
				name={experienceName}
				contentId={contentId}
				extensionKey={extensionKey}
				mode={mode}
				attributes={attributes}
			/>
			<TasksReportTable
				tasks={visibleTasks}
				columns={params.columns}
				currentSortBy={sortState.sortBy}
				currentReverseSort={sortState.reverseSort}
				pageSize={params.pageSize}
				hasNextPage={hasNextPage}
				loadingMore={loading}
				taskUpdateTrigger={taskUpdateTrigger}
				onSort={handleSort}
				onLoadMore={loadMore}
				onTaskComplete={handleTaskComplete}
			/>
		</Box>
	);
};

export const TasksReportMacroRenderer = (
	props: TasksReportMacroRendererProps,
): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const renderErrorFallback = useCallback(
		({ error }: { error: Error }) => (
			<ErrorDisplay error={error}>{formatMessage(i18n.errorBoundaryFallback)}</ErrorDisplay>
		),
		[formatMessage],
	);

	return (
		<GenericErrorBoundary
			renderOnError={renderErrorFallback}
			attribution={Attribution.PAGE_EXPERIENCES}
		>
			<TasksReportMacroRendererComponent {...props} />
		</GenericErrorBoundary>
	);
};
